import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, ArrowUpDown, ExternalLink, Trash2, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatDate } from "@/lib/dateUtils";
import { useProjectsQuery, useDeleteProjectMutation, useMyProjectRolesQuery } from "@/hooks/queries/useProjectsQuery";
import type { DeleteProjectCommand, Project } from "@/types/project";
import {
  ProjectStatus,
  ProjectPhase,
  RiskLevel,
  RoleType,
  ReportGenerationStatus,
} from "@/types/project";
import RedactedValue from "@/components/project/RedactedValue";

interface ProjectDeleteButtonProps {
  project: Project;
  myRoles: Record<number, RoleType>;
  onDelete: (project: Project) => void;
}

const ProjectDeleteButton = ({ project, myRoles, onDelete }: ProjectDeleteButtonProps) => {
  const isOwner = myRoles[project.id] === RoleType.Owner;

  if (!isOwner) return null;
  if (project.reportGenerationStatus === ReportGenerationStatus.Generating) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="gap-1 text-destructive hover:text-destructive"
      onClick={(e) => {
        e.stopPropagation();
        onDelete(project);
      }}
    >
      <Trash2 size={14} />
    </Button>
  );
};

const ProjectPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tz = useUserTimezone();
  const { t } = useTranslation(language);

  const { data: projects = [], isLoading: loading } = useProjectsQuery();
  const { data: myRoles = {} } = useMyProjectRolesQuery();
  const deleteProjectMutation = useDeleteProjectMutation();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [phaseFilter, setPhaseFilter] = useState<string>("all");
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const command: DeleteProjectCommand = { id: deleteTarget.id, rowVersion: deleteTarget.rowVersion };
      await deleteProjectMutation.mutateAsync({ id: deleteTarget.id, command });
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error(error instanceof Error ? error.message : t("deleteHasCreditsError"));
    } finally {
      setDeleteTarget(null);
    }
  };

  const requestDelete = (project: Project) => {
    setDeleteTarget(project);
  };

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getStatusLabel = (status: ProjectStatus | null) => {
    if (status === null || status === undefined) return "-";
    switch (status) {
      case ProjectStatus.Active: return t("active");
      case ProjectStatus.OnHold: return t("onHold");
      case ProjectStatus.Cancelled: return t("cancelled");
      case ProjectStatus.Done: return t("done");
      default: return "-";
    }
  };

  const getPhaseLabel = (phase: ProjectPhase | null) => {
    if (phase === null || phase === undefined) return "-";
    switch (phase) {
      case ProjectPhase.InitialReview: return t("initialReview");
      case ProjectPhase.DueDiligence: return t("dueDiligence");
      case ProjectPhase.Negotiation: return "Negotiation";
      case ProjectPhase.Closing: return t("closing");
      default: return "-";
    }
  };

  const getRiskLabel = (risk: RiskLevel | null) => {
    if (risk === null || risk === undefined) return "-";
    switch (risk) {
      case RiskLevel.Low: return t("low");
      case RiskLevel.Medium: return t("medium");
      case RiskLevel.High: return t("high");
      default: return "-";
    }
  };

  const getProjectValue = (project: Project, key: string): string | number | null => {
    switch (key) {
      case "name": return project.name || project.targetName;
      case "status": return project.status;
      case "phase": return project.phase;
      case "risk": return project.risk;
      case "redacted": return project.redacted;
      case "expectedClose": return project.expectedClose;
      case "owner": return project.owner;
      default: return null;
    }
  };

  const filteredAndSortedProjects = [...projects]
    .filter((project) => {
      const matchesSearch =
        (project.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (project.targetName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (project.owner?.toLowerCase() || "").includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || project.status === Number(statusFilter);
      const matchesPhase = phaseFilter === "all" || project.phase === Number(phaseFilter);
      const matchesRisk = riskFilter === "all" || project.risk === Number(riskFilter);
      return matchesSearch && matchesStatus && matchesPhase && matchesRisk;
    })
    .sort((a, b) => {
      if (!sortConfig) return 0;
      const { key, direction } = sortConfig;
      const aVal = getProjectValue(a, key);
      const bVal = getProjectValue(b, key);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

  const StatusLamp = ({ status }: { status: ProjectStatus | null }) => {
    if (status === null || status === undefined) return <span>-</span>;
    const colors: Record<number, string> = {
      [ProjectStatus.Active]: "bg-green-500",
      [ProjectStatus.OnHold]: "bg-yellow-500",
      [ProjectStatus.Cancelled]: "bg-red-500",
      [ProjectStatus.Done]: "bg-blue-500",
    };
    return (
      <div className="flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full ${colors[status] || "bg-gray-400"}`} />
        <span>{getStatusLabel(status)}</span>
      </div>
    );
  };

  const RiskBadge = ({ risk }: { risk: RiskLevel | null }) => {
    if (risk === null || risk === undefined) return <span>-</span>;
    const colors: Record<number, string> = {
      [RiskLevel.Low]: "bg-green-100 text-green-800",
      [RiskLevel.Medium]: "bg-yellow-100 text-yellow-800",
      [RiskLevel.High]: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[risk] || ""}`}>
        {getRiskLabel(risk)}
      </span>
    );
  };

  const SortableHeader = ({ column, label }: { column: string; label: string }) => (
    <TableHead>
      <button
        className="flex items-center gap-1 hover:text-foreground transition-colors"
        onClick={() => handleSort(column)}
      >
        {label}
        <ArrowUpDown size={14} className="opacity-50" />
      </button>
    </TableHead>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("dueDiligence")}</h1>
            <p className="text-muted-foreground">{t("dashboardSubtitle")}</p>
          </div>
          <Button className="w-full gap-2 sm:w-auto" onClick={() => navigate("/project/add")}>
            <Plus size={18} />
            {t("addProject")}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 p-4 bg-card rounded-lg border border-border">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("status")}</SelectItem>
              <SelectItem value={String(ProjectStatus.Active)}>{t("active")}</SelectItem>
              <SelectItem value={String(ProjectStatus.OnHold)}>{t("onHold")}</SelectItem>
              <SelectItem value={String(ProjectStatus.Cancelled)}>{t("cancelled")}</SelectItem>
              <SelectItem value={String(ProjectStatus.Done)}>{t("done")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={phaseFilter} onValueChange={setPhaseFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder={t("phase")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("phase")}</SelectItem>
              <SelectItem value={String(ProjectPhase.InitialReview)}>{t("initialReview")}</SelectItem>
              <SelectItem value={String(ProjectPhase.DueDiligence)}>{t("dueDiligence")}</SelectItem>
              <SelectItem value={String(ProjectPhase.Negotiation)}>Negotiation</SelectItem>
              <SelectItem value={String(ProjectPhase.Closing)}>{t("closing")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder={t("risk")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("risk")}</SelectItem>
              <SelectItem value={String(RiskLevel.Low)}>{t("low")}</SelectItem>
              <SelectItem value={String(RiskLevel.Medium)}>{t("medium")}</SelectItem>
              <SelectItem value={String(RiskLevel.High)}>{t("high")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-card rounded-lg border border-border">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-muted-foreground" size={32} />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="name" label={t("projectTarget")} />
                  <SortableHeader column="status" label={t("status")} />
                  <SortableHeader column="phase" label={t("phase")} />
                  <SortableHeader column="risk" label={t("risk")} />
                  <SortableHeader column="redacted" label={t("redacted")} />
                  <SortableHeader column="expectedClose" label={t("expectedCloseDate")} />
                  <SortableHeader column="owner" label={t("owner")} />
                  <TableHead className="w-[140px]">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedProjects.map((project) => (
                  <TableRow
                    key={project.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onDoubleClick={() => navigate(`/project/${project.id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div>
                          <div>{project.name}</div>
                          {project.targetName && project.targetName !== project.name && (
                            <div className="text-xs text-muted-foreground">{project.targetName}</div>
                          )}
                        </div>
                        {project.reportGenerationStatus === ReportGenerationStatus.Generating && (
                          <Loader2 className="animate-spin text-muted-foreground" size={16} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell><StatusLamp status={project.status} /></TableCell>
                    <TableCell>{getPhaseLabel(project.phase)}</TableCell>
                    <TableCell><RiskBadge risk={project.risk} /></TableCell>
                    <TableCell>
                      <RedactedValue value={project.redacted} />
                    </TableCell>
                    <TableCell>
                      {project.expectedClose
                        ? formatDate(project.expectedClose, tz, language)
                        : "-"}
                    </TableCell>
                    <TableCell>{project.owner || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={() => navigate(`/project/${project.id}`)}
                        >
                          <ExternalLink size={14} />
                        </Button>
                        <ProjectDeleteButton project={project} myRoles={myRoles} onDelete={requestDelete} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAndSortedProjects.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {t("noProjectsFound")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteProject")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDeleteProjectDesc")} <strong>{deleteTarget?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteProjectMutation.isPending}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteProjectMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteProjectMutation.isPending ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default ProjectPage;
